<script lang="ts">
  import { orgAiContent } from "@nrk/origo";
  import VisibleForEndUsers from "./VisibleForEndUsers.svelte";
  import InfoBox from "./InfoBox.svelte";
  import { afterUpdate } from "svelte";
  import { pageState } from "../../state/page";
  import type { Image } from "bildebanken-model";
  import {
    getGeneratedAltTextFromBuffer,
    getGeneratedAltTextFromUrl,
  } from "../../services/cognitive";

  export let altTextRequired: boolean = false;
  export let altText: string | undefined = undefined;
  export let editingSingleImage: boolean = false;
  export let getAltTextSuggestionOnDemand: boolean = false;
  export let altTextUpdate = (text: string) => {};
  export let image: Image;
  export let detailMode: boolean = false;

  let textareaElement: HTMLTextAreaElement;

  let altTextSuggestion: string | undefined = undefined;

  let fetchingAltTextSuggestion: boolean = false;

  afterUpdate(() => {
    resizeTextarea();
  });

  function resizeTextarea() {
    if (textareaElement) {
      textareaElement.style.height = "auto";
      textareaElement.style.height = textareaElement.scrollHeight + "px";
    }
  }

  const altTextDisclaimer = "(Bildebeskrivelsen er laget av en KI-tjeneste)";
  let altTextAltered = false;

  $: isUsingSuggestion = altText === altTextSuggestion + " " + altTextDisclaimer;

  $: hasThumbnail = !!image.thumbnail && $pageState.type === "UPLOAD";

  $: showSpinner = fetchingAltTextSuggestion && editingSingleImage;
  $: showGetSuggestionButton =
    !fetchingAltTextSuggestion &&
    editingSingleImage &&
    ($pageState.type === "EDIT" || hasThumbnail || getAltTextSuggestionOnDemand);
  $: showAiWarning = isUsingSuggestion && !altTextAltered && !fetchingAltTextSuggestion;

  function onTextFieldInput() {
    return function (this: HTMLTextAreaElement) {
      if (this.value !== altTextSuggestion) {
        altTextAltered = true;
      }
      altTextUpdate(this.value);
    };
  }

  async function handleGetSuggestion() {
    fetchingAltTextSuggestion = true;
    try {
      let altTextSuggestionLocal: string | undefined;
      if ($pageState.type === "EDIT" || $pageState.type === "DETAIL") {
        const proxyUrl = image.proxy;
        if (proxyUrl) {
          if (typeof proxyUrl === "string") {
            const altText = await getGeneratedAltTextFromUrl(proxyUrl);
            altTextSuggestionLocal = altText.caption.nb;
          } else {
            altTextSuggestionLocal = undefined;
          }
        } else {
          altTextSuggestionLocal = altText;
        }
      } else if (hasThumbnail && typeof image.thumbnail === "string") {
        const response = await fetch(image.thumbnail);
        if (!response.ok) {
          throw Error("Could not fetch image thumbnail");
        }
        const imageBlob = await response.blob();
        const imageCaption = await getGeneratedAltTextFromBuffer(imageBlob);
        altTextSuggestionLocal = imageCaption.caption.nb;
      }
      altTextAltered = false;
      if (altTextSuggestionLocal) {
        altTextSuggestion = altTextSuggestionLocal;
        altTextUpdate(altTextSuggestionLocal + " " + altTextDisclaimer);
      }
    } catch (error) {
      console.error("Kunne ikke generere alternativ tekst: ", error);
    } finally {
      fetchingAltTextSuggestion = false;
    }
  }
</script>

<div style="margin:1px;">
  <label for="altText" class:sr-only={detailMode}>
    Alternativ tekst
    {#if altTextRequired}
      <span>*</span>
    {/if}
    <VisibleForEndUsers />
  </label>

  {#if !detailMode}
    <InfoBox
      header="Hva er alternativ tekst?"
      link="https://portalen.nrk.no/nett/nettpublisering/?/bildebeskrivelse"
    >
      <p class="content">
        Alternativ tekst skal beskrive og tilgjengeliggjøre innholdet for alle – spesielt personer
        som bruker skjermleser eller hvis bildet ikke lastes inn.
      </p>
    </InfoBox>
  {/if}
  <div class="input-wrapper">
    <textarea
      bind:this={textareaElement}
      class={`org-input${showAiWarning ? " ai-suggest-input" : ""}`}
      aria-describedby="altTextSuggestion"
      id="altText"
      data-testid="alt-text-textarea"
      value={altText || ""}
      placeholder="Beskrivelse for publikum som ikke kan se bildet"
      aria-invalid={altTextRequired && !altText}
      on:input={onTextFieldInput()}
    ></textarea>
    <span class="org-warning">Alternativ tekst må fylles ut</span>
  </div>
  {#if showAiWarning}
    <span
      id="altTextSuggestion"
      class="ai-suggest-warning"
      data-testid="alt-text-ai-suggestion-info"
    >
      {@html orgAiContent} Forslaget er generert av KI (Gemini)
    </span>
  {/if}
  <div class="suggest-button">
    {#if showSpinner}
      <span style="margin-top: var(--org-small);">
        Henter KI-forslag..
        <span style="padding-left:4em;" class="org-spinner" />
      </span>
    {/if}
    {#if showGetSuggestionButton}
      <button
        class="org-tag org-tag--suggestive"
        style="margin: var(--org-small) 0"
        data-testid="alt-text-ai-suggest-button"
        type="button"
        on:click={handleGetSuggestion}
      >
        {@html orgAiContent}
        {showAiWarning ? "Bytt" : "Legg til"} KI-generert forslag
      </button>
    {/if}
  </div>
</div>

<style>
  .ai-status {
    padding-top: var(--org-small);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .label-row {
    display: flex;
    align-items: center;
    gap: var(--org-xsmall);
  }

  .input-wrapper {
    position: relative;
  }

  .suggest-button {
    position: relative;
  }

  .suggest-button {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }

  textarea.org-warning {
    position: absolute;
    top: 100%;
    left: 0;
  }
  textarea.ai-suggest-warning {
    color: var(--org-color-black);
    background: var(--org-color-unsaved);
    padding: 0.5em 0.5em 0.5em 0.2em;
    border-radius: 2px;
    font-weight: 400;
    font-size: var(--org-font-small, 12px);
    line-height: 1;
  }

  .ai-suggest-input {
    outline-color: var(--org-color-unsaved);
    outline-width: 1px;
    outline-style: dashed;
    box-shadow: none;
  }

  .ai-suggest-input:focus {
    outline-style: none;
  }

  .content {
    font-size: 12px;
    margin-top: 6px;
    margin-bottom: 6px;
  }

  .org-spinner {
    font-size: 0.5em;
    margin-right: 18px;
    padding-top: 3px;
  }
  .alttext-wrapper {
    margin-bottom: var(--org-medium);
  }
</style>
